import React from "react"
import { useTranslation } from "react-i18next"
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  XAxis,
  YAxis
} from "recharts"

import {
  CHART_AXIS_TICK,
  CHART_GRID_STROKE,
  CHART_TOOLTIP_LABEL_STYLE,
  CHART_TOOLTIP_STYLE
} from "../global-map-surface.ts"
import type { AnalyticsActivityTrend } from "../redux/global-map.types"
import { ANALYTICS_ENTITY_COLOR } from "./analytics-constants"

interface ActivityTrendChartProps {
  trend: AnalyticsActivityTrend
}

const SERIES_LABEL: Record<string, string> = {
  lead: "Leads",
  project: "Projects",
  subcontractor: "Subcontractors",
  appointment: "Appointments"
}

const parseBucketDate = (value: string): Date => new Date(`${value}T00:00:00`)

function formatBucketLabel(
  value: string,
  bucketSize: AnalyticsActivityTrend["bucketSize"]
): string {
  const parsed = parseBucketDate(value)
  if (Number.isNaN(parsed.getTime())) return value
  if (bucketSize === "month")
    return parsed.toLocaleDateString(undefined, { month: "short" })
  return parsed.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short"
  })
}

/**
 * Spec §6: qualifying real events (not creation dates alone — see
 * `analytics.py`'s own docstring for the exact source per series), bucketed
 * by day/week/month depending on the selected period's length. Workforce
 * has no event source at all and is correctly absent from `points[].counts`
 * — `unavailableEntities` names it so the legend can say so rather than
 * silently omitting it with no explanation.
 */
export function ActivityTrendChart({ trend }: ActivityTrendChartProps) {
  const { t } = useTranslation()
  const seriesKeys = Array.from(
    new Set(trend.points.flatMap((point) => Object.keys(point.counts)))
  )

  const chartData = trend.points.map((point) => ({
    bucket: point.bucket,
    label: formatBucketLabel(point.bucket, trend.bucketSize),
    ...point.counts
  }))

  return (
    <div>
      <ResponsiveContainer width="100%" height={220}>
        <LineChart
          data={chartData}
          margin={{ top: 8, right: 8, left: -16, bottom: 0 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            stroke={CHART_GRID_STROKE}
          />
          <XAxis
            dataKey="label"
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 11, fill: "#64748B" }}
            minTickGap={24}
            tickMargin={8}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tick={CHART_AXIS_TICK}
            allowDecimals={false}
            width={32}
          />
          <RechartsTooltip
            contentStyle={CHART_TOOLTIP_STYLE}
            labelStyle={CHART_TOOLTIP_LABEL_STYLE}
            cursor={{ stroke: "#CBD5E1", strokeWidth: 1 }}
          />
          <Legend
            wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
            iconType="circle"
            iconSize={8}
          />
          {seriesKeys.map((key) => {
            const color = ANALYTICS_ENTITY_COLOR[key] ?? "#94A3B8"
            return (
              <Line
                key={key}
                type="monotone"
                dataKey={key}
                name={SERIES_LABEL[key] ?? key}
                stroke={color}
                strokeWidth={2}
                // Small white-ringed points read the series apart where two
                // lines cross, without the weight of a thicker stroke.
                dot={{ r: 3, fill: color, stroke: "#fff", strokeWidth: 1.5 }}
                activeDot={{
                  r: 5,
                  fill: color,
                  stroke: "#fff",
                  strokeWidth: 2
                }}
                isAnimationActive={false}
              />
            )
          })}
        </LineChart>
      </ResponsiveContainer>
      {trend.unavailableEntities.length > 0 && (
        <p className="m-0 mt-1 text-[11px] leading-snug text-slate-400">
          {t("globalMap.analytics.activityTrend.workforceNotTracked")}
        </p>
      )}
    </div>
  )
}
